import * as Collapsible from '@radix-ui/react-collapsible'
import { ChevronDown } from 'lucide-react'
import { useState } from 'react'
import type { ProblemSource } from '../types'
import { ClarificationsSection } from './clarifications-section'
import { ConstraintsSection } from './constraints-section'
import { HintsSection } from './hints-section'
import { StatementPreview } from './statement-preview'
import type { TestConfiguration } from './tests-section'

type TabId = 'write' | 'preview'

interface StatementSectionProps {
  statement: string
  onUpdateStatement: (statement: string) => void
  title: string
  testConfig: TestConfiguration
  constraints: { id?: string; content: string; order: number }[]
  onUpdateConstraints: (
    constraints: { id?: string; content: string; order: number }[],
  ) => void
  clarifications: {
    id?: string
    question: string
    answer: string
    order: number
  }[]
  onUpdateClarifications: (
    clarifications: {
      id?: string
      question: string
      answer: string
      order: number
    }[],
  ) => void
  hints: { id?: string; content: string; order: number }[]
  onUpdateHints: (
    hints: { id?: string; content: string; order: number }[],
  ) => void
  /** Current sources, so the preview credits them as the learner will see. */
  sources?: ProblemSource[]
}

function CollapsiblePanel({
  title,
  count,
  defaultOpen = true,
  children,
}: {
  title: string
  count: number
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <Collapsible.Root open={open} onOpenChange={setOpen}>
      <Collapsible.Trigger asChild>
        <button
          type="button"
          className="bg-muted/50 hover:bg-muted flex w-full items-center justify-between rounded-lg px-4 py-3 transition-colors"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{title}</span>
            {count > 0 && (
              <span className="bg-muted-foreground/20 text-muted-foreground rounded-full px-2 py-0.5 text-xs font-medium">
                {count}
              </span>
            )}
          </div>
          <ChevronDown
            className={`text-muted-foreground h-4 w-4 transition-transform ${open ? '' : '-rotate-90'}`}
          />
        </button>
      </Collapsible.Trigger>
      <Collapsible.Content className="pt-3">{children}</Collapsible.Content>
    </Collapsible.Root>
  )
}

export function StatementSection({
  statement,
  onUpdateStatement,
  title,
  testConfig,
  constraints,
  onUpdateConstraints,
  clarifications,
  onUpdateClarifications,
  hints,
  onUpdateHints,
  sources,
}: StatementSectionProps) {
  const [activeTab, setActiveTab] = useState<TabId>('write')

  return (
    <div className="space-y-6">
      {/* Problem Statement */}
      <CollapsiblePanel title="Problem Statement" count={0} defaultOpen>
        <div>
          <p className="text-muted-foreground mb-3 text-sm">
            Write the problem description in Markdown format. This will be shown
            to users when they view the problem.
          </p>

          {/* Tab buttons */}
          <div className="mb-2 flex gap-1">
            <button
              type="button"
              onClick={() => setActiveTab('write')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                activeTab === 'write'
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Write
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('preview')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                activeTab === 'preview'
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Preview
            </button>
          </div>

          {/* Content area */}
          {activeTab === 'write' ? (
            <textarea
              value={statement}
              onChange={(e) => onUpdateStatement(e.target.value)}
              placeholder="Write the problem statement here...

You can use Markdown formatting:
- **bold text**
- *italic text*
- `inline code`
- ```code blocks```
- Lists, links, etc."
              className="border-input bg-background min-h-[400px] w-full resize-y rounded-lg border px-3 py-2 font-mono text-sm"
            />
          ) : (
            <div className="border-input min-h-[400px] w-full rounded-lg border">
              <StatementPreview
                title={title}
                statement={statement}
                testConfig={testConfig}
                constraints={constraints}
                sources={sources}
              />
            </div>
          )}

          <div className="text-muted-foreground mt-2 text-xs">
            <strong>Tip:</strong> The statement should describe the problem
            clearly, including what the function should do and any constraints
            on the input.
          </div>
        </div>
      </CollapsiblePanel>

      {/* Constraints */}
      <CollapsiblePanel
        title="Constraints"
        count={constraints.length}
        defaultOpen
      >
        <ConstraintsSection
          constraints={constraints}
          onUpdate={onUpdateConstraints}
        />
      </CollapsiblePanel>

      {/* Clarifications */}
      <CollapsiblePanel
        title="Clarifications"
        count={clarifications.length}
        defaultOpen
      >
        <ClarificationsSection
          clarifications={clarifications}
          onUpdate={onUpdateClarifications}
        />
      </CollapsiblePanel>

      {/* Hints */}
      <CollapsiblePanel title="Hints" count={hints.length} defaultOpen>
        <HintsSection hints={hints} onUpdate={onUpdateHints} />
      </CollapsiblePanel>
    </div>
  )
}
